import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/user-manual.css';

const UserManual = ({ isOpen, onClose }) => {
  const { userRole } = useAuth();
  const [activeSection, setActiveSection] = useState('dashboard');

  if (!isOpen) return null;

  const adminManualSections = {
    dashboard: {
      title: 'Dashboard',
      content: `
        <h3>Admin Dashboard Overview</h3>
        <p>The admin dashboard provides a comprehensive overview of your landscaping business operations.</p>
        <ul>
          <li><strong>Key Metrics:</strong> View total appointments, pending approvals, approved and completed appointments at a glance.</li>
          <li><strong>Recent Activity:</strong> See the most recent appointments, estimates, and support tickets.</li>
          <li><strong>Quick Actions:</strong> Approve or reject appointments directly from the dashboard.</li>
          <li><strong>Filtering:</strong> Use the tab filters to quickly view appointments by status.</li>
        </ul>
      `
    },
    estimates: {
      title: 'Estimates',
      content: `
        <h3>Managing Estimates</h3>
        <p>The Estimates section allows you to handle all client estimate requests.</p>
        <ul>
          <li><strong>Create Estimates:</strong> Generate custom estimates for clients with itemized services and pricing.</li>
          <li><strong>Review Requests:</strong> View all pending estimate requests from clients.</li>
          <li><strong>Send Quotes:</strong> Prepare and send professional quotes to clients.</li>
          <li><strong>Track Status:</strong> Monitor which estimates have been approved, quoted, or declined.</li>
          <li><strong>Convert to Appointments:</strong> Easily convert approved estimates into scheduled appointments.</li>
        </ul>
      `
    },
    appointments: {
      title: 'Appointments',
      content: `
        <h3>Managing Appointments</h3>
        <p>The Appointments section is your central hub for managing client bookings.</p>
        <ul>
          <li><strong>View All Appointments:</strong> See a complete list of appointments with filtering options.</li>
          <li><strong>Appointment Approval:</strong> Review and approve or reject appointment requests.</li>
          <li><strong>Scheduling:</strong> Manage date and time slots for services.</li>
          <li><strong>Service Details:</strong> View comprehensive details about each appointment.</li>
          <li><strong>Status Updates:</strong> Mark appointments as confirmed, completed, or cancelled.</li>
          <li><strong>Client Communication:</strong> Send appointment confirmation and reminder messages to clients.</li>
        </ul>
      `
    },
    calendar: {
      title: 'Calendar',
      content: `
        <h3>Calendar & Slot Management</h3>
        <p>The Calendar provides a visual overview of your schedule and allows detailed time slot management.</p>
        <ul>
          <li><strong>Visual Calendar:</strong> View daily, weekly, and monthly schedule views.</li>
          <li><strong>Time Slot Creation:</strong> Create available time slots for client bookings.</li>
          <li><strong>Block Time:</strong> Reserve time for internal tasks or block unavailable periods.</li>
          <li><strong>Recurring Slots:</strong> Set up regular availability patterns to save time.</li>
          <li><strong>Team Allocation:</strong> Assign appointments to specific team members.</li>
          <li><strong>Capacity Planning:</strong> Manage workload by controlling available slots.</li>
        </ul>
      `
    },
    messages: {
      title: 'Messages',
      content: `
        <h3>Client Messaging</h3>
        <p>The Messaging system allows direct communication with your clients.</p>
        <ul>
          <li><strong>Inbox Management:</strong> View and respond to client messages.</li>
          <li><strong>Message History:</strong> Access complete conversation histories with each client.</li>
          <li><strong>File Sharing:</strong> Exchange documents, images, and files with clients.</li>
          <li><strong>Templates:</strong> Use message templates for common responses.</li>
          <li><strong>Notifications:</strong> Receive alerts for new messages.</li>
          <li><strong>Bulk Messaging:</strong> Send important announcements to multiple clients.</li>
        </ul>
      `
    },
    customers: {
      title: 'Customers',
      content: `
        <h3>Customer Management</h3>
        <p>The Customers section provides tools to manage your client database.</p>
        <ul>
          <li><strong>Client Directory:</strong> View a complete list of all customers with contact information.</li>
          <li><strong>Client Profiles:</strong> Access detailed profiles with service history and preferences.</li>
          <li><strong>Customer Segmentation:</strong> Group customers by location, service type, or value.</li>
          <li><strong>Activity History:</strong> Review each client's complete appointment and estimate history.</li>
          <li><strong>Notes & Tags:</strong> Add custom notes and tags to client profiles for better organization.</li>
        </ul>
      `
    },
    support: {
      title: 'Support Tickets',
      content: `
        <h3>Support Ticket Management</h3>
        <p>The Support Tickets section allows you to handle client inquiries and issues.</p>
        <ul>
          <li><strong>Ticket Dashboard:</strong> View all open, in-progress, and resolved tickets.</li>
          <li><strong>Priority Handling:</strong> Sort tickets by urgency and type.</li>
          <li><strong>Response Management:</strong> Reply to tickets and track response times.</li>
          <li><strong>Issue Resolution:</strong> Document solutions and mark tickets as resolved.</li>
          <li><strong>Knowledge Base:</strong> Build a database of common issues and solutions.</li>
          <li><strong>Performance Metrics:</strong> Track response times and customer satisfaction.</li>
        </ul>
      `
    },
    trees: {
      title: 'My Trees',
      content: `
        <h3>Tree Health Management</h3>
        <p>The My Trees section helps you track and manage tree health for clients.</p>
        <ul>
          <li><strong>Tree Inventory:</strong> Maintain a database of client trees with species information.</li>
          <li><strong>Health Assessments:</strong> Record and track tree health status and issues.</li>
          <li><strong>Treatment Records:</strong> Document treatments and interventions for each tree.</li>
          <li><strong>Scheduled Care:</strong> Plan and schedule routine tree maintenance.</li>
          <li><strong>Health Alerts:</strong> Set up notifications for necessary inspections or treatments.</li>
          <li><strong>Species Information:</strong> Access a database of tree species and care requirements.</li>
        </ul>
      `
    },
    analytics: {
      title: 'Analytics',
      content: `
        <h3>Business Analytics</h3>
        <p>The Analytics section provides insights into your business performance.</p>
        <ul>
          <li><strong>Revenue Reports:</strong> Track income by service type, time period, and location.</li>
          <li><strong>Appointment Analytics:</strong> Analyze booking patterns, popular services, and peak times.</li>
          <li><strong>Customer Metrics:</strong> Monitor client acquisition, retention, and lifetime value.</li>
          <li><strong>Service Performance:</strong> Evaluate which services generate the most revenue.</li>
          <li><strong>Team Productivity:</strong> Track staff performance and efficiency.</li>
          <li><strong>Growth Trends:</strong> View historical data and projections for business growth.</li>
          <li><strong>Exportable Reports:</strong> Generate detailed reports for business planning.</li>
        </ul>
      `
    },
    seasonal: {
      title: 'Seasonal Planning',
      content: `
        <h3>Seasonal Planning Tools</h3>
        <p>The Seasonal Planning section helps you prepare for seasonal landscaping needs.</p>
        <ul>
          <li><strong>Seasonal Calendar:</strong> View and plan services appropriate for each season.</li>
          <li><strong>Weather Integration:</strong> Access weather forecasts to plan seasonal activities.</li>
          <li><strong>Service Recommendations:</strong> Get suggestions for seasonal services to promote.</li>
          <li><strong>Resource Allocation:</strong> Plan staff and equipment needs for seasonal demands.</li>
          <li><strong>Marketing Campaigns:</strong> Prepare seasonal marketing initiatives for your services.</li>
          <li><strong>Inventory Planning:</strong> Manage supplies and materials needed for seasonal work.</li>
        </ul>
      `
    },
    profile: {
      title: 'Profile',
      content: `
        <h3>Admin Profile Management</h3>
        <p>The Profile section allows you to manage your admin account settings.</p>
        <ul>
          <li><strong>Personal Information:</strong> Update your name, contact details, and profile image.</li>
          <li><strong>Security Settings:</strong> Change password and manage account security.</li>
          <li><strong>Notification Preferences:</strong> Customize which alerts you receive and how.</li>
          <li><strong>Admin Access:</strong> Manage administration privileges and access levels.</li>
          <li><strong>Connected Accounts:</strong> Link to other business tools and services.</li>
        </ul>
      `
    }
  };

  const clientManualSections = {
    dashboard: {
      title: 'Dashboard',
      content: `
        <h3>Client Dashboard Overview</h3>
        <p>Your dashboard provides a summary of your landscaping services and activities.</p>
        <ul>
          <li><strong>Upcoming Appointments:</strong> View your scheduled landscaping services.</li>
          <li><strong>Estimate Status:</strong> Check the status of your requested estimates.</li>
          <li><strong>Recent Activity:</strong> See your recent interactions and service history.</li>
          <li><strong>Quick Actions:</strong> Access common tasks like booking services or requesting estimates.</li>
          <li><strong>Service Reminders:</strong> Get notifications about upcoming maintenance needs.</li>
          <li><strong>Weather Updates:</strong> See local weather forecasts relevant to your scheduled services.</li>
        </ul>
      `
    },
    bookService: {
      title: 'Book Service',
      content: `
        <h3>Booking Landscaping Services</h3>
        <p>Easily schedule landscaping services based on your specific needs.</p>
        <ul>
          <li><strong>Service Selection:</strong> Choose from a variety of landscaping services including lawn care, tree trimming, garden maintenance, and more.</li>
          <li><strong>Available Time Slots:</strong> View available appointment dates and times based on the service provider's schedule.</li>
          <li><strong>Date & Time Selection:</strong> Select your preferred date and time for the service.</li>
          <li><strong>Service Details:</strong> Add specific instructions or requirements for your appointment.</li>
          <li><strong>Recurring Service:</strong> Set up regular maintenance schedules (weekly, bi-weekly, monthly).</li>
          <li><strong>Confirmation:</strong> Receive immediate confirmation of your booking request.</li>
        </ul>
      `
    },
    appointments: {
      title: 'My Appointments',
      content: `
        <h3>Managing Your Appointments</h3>
        <p>Keep track of all your scheduled landscaping services in one place.</p>
        <ul>
          <li><strong>Appointment List:</strong> View all your upcoming and past appointments.</li>
          <li><strong>Appointment Details:</strong> Access comprehensive information about each service booking.</li>
          <li><strong>Status Tracking:</strong> Monitor whether appointments are pending, confirmed, in progress, or completed.</li>
          <li><strong>Reschedule:</strong> Easily change the date or time of an upcoming appointment if needed.</li>
          <li><strong>Cancellation:</strong> Cancel appointments with appropriate notice if necessary.</li>
          <li><strong>Service History:</strong> Review past services, including details and outcomes.</li>
          <li><strong>Feedback:</strong> Provide ratings and comments after service completion.</li>
        </ul>
      `
    },
    estimates: {
      title: 'Estimates',
      content: `
        <h3>Requesting and Managing Estimates</h3>
        <p>Get detailed cost estimates for landscaping projects and services.</p>
        <ul>
          <li><strong>Request Estimates:</strong> Submit details about your landscaping project to receive a cost estimate.</li>
          <li><strong>Project Description:</strong> Provide information about the scope, size, and requirements of your project.</li>
          <li><strong>Photo Upload:</strong> Share images of the area that needs landscaping work.</li>
          <li><strong>Track Status:</strong> Monitor the status of your estimate requests (pending, in progress, completed).</li>
          <li><strong>Review Estimates:</strong> Access detailed breakdowns of costs for materials, labor, and other expenses.</li>
          <li><strong>Accept/Decline:</strong> Easily approve estimates to convert them into scheduled appointments.</li>
          <li><strong>Estimate History:</strong> Access an archive of all previous estimate requests and responses.</li>
        </ul>
      `
    },
    messages: {
      title: 'Messages',
      content: `
        <h3>Communication Center</h3>
        <p>Direct communication with your landscaping service provider.</p>
        <ul>
          <li><strong>Inbox:</strong> Access all your messages and conversations in one place.</li>
          <li><strong>New Message:</strong> Initiate new conversations with your service provider.</li>
          <li><strong>Appointment Questions:</strong> Ask specific questions about upcoming appointments.</li>
          <li><strong>Photo Sharing:</strong> Exchange images related to your landscaping needs.</li>
          <li><strong>Document Sharing:</strong> Send and receive important documents such as contracts or care instructions.</li>
          <li><strong>Notifications:</strong> Receive alerts when new messages arrive.</li>
          <li><strong>Message History:</strong> Review past conversations for reference.</li>
        </ul>
      `
    },
    support: {
      title: 'Support Tickets',
      content: `
        <h3>Customer Support System</h3>
        <p>Get assistance with any issues or questions regarding your landscaping services.</p>
        <ul>
          <li><strong>Create Tickets:</strong> Submit detailed support requests for specific issues.</li>
          <li><strong>Issue Categories:</strong> Categorize your support needs (billing, service quality, scheduling, etc.).</li>
          <li><strong>Priority Selection:</strong> Indicate the urgency of your support request.</li>
          <li><strong>Track Status:</strong> Monitor the progress of your support tickets (open, in progress, resolved).</li>
          <li><strong>Response History:</strong> View all communications related to your support tickets.</li>
          <li><strong>FAQ Access:</strong> Quick access to frequently asked questions and solutions.</li>
          <li><strong>Resolution Confirmation:</strong> Verify when your issue has been successfully resolved.</li>
        </ul>
      `
    },
    notifications: {
      title: 'Notifications',
      content: `
        <h3>Personalized Alert System</h3>
        <p>Stay informed about important updates and activities related to your landscaping services.</p>
        <ul>
          <li><strong>Service Reminders:</strong> Receive notifications about upcoming appointments.</li>
          <li><strong>Estimate Updates:</strong> Get alerts when new estimates are provided.</li>
          <li><strong>Message Alerts:</strong> Be notified when you receive new messages.</li>
          <li><strong>Support Ticket Updates:</strong> Stay informed about changes to your support requests.</li>
          <li><strong>Seasonal Recommendations:</strong> Receive tips and service suggestions based on the current season.</li>
          <li><strong>Weather Alerts:</strong> Get notifications about weather conditions that might affect your scheduled services.</li>
          <li><strong>Preferences:</strong> Customize which notifications you receive and how (email, app, text).</li>
        </ul>
      `
    },
    profile: {
      title: 'Profile',
      content: `
        <h3>Managing Your Account</h3>
        <p>Access and update your personal information and account preferences.</p>
        <ul>
          <li><strong>Personal Information:</strong> Update your name, contact details, and address.</li>
          <li><strong>Property Details:</strong> Manage information about your landscaping location(s).</li>
          <li><strong>Payment Methods:</strong> Add, edit, or remove payment options for services.</li>
          <li><strong>Notification Settings:</strong> Control how and when you receive notifications.</li>
          <li><strong>Password Management:</strong> Change your password and manage security settings.</li>
          <li><strong>Communication Preferences:</strong> Set your preferred methods of contact.</li>
          <li><strong>Account History:</strong> Review your service history and account activity.</li>
        </ul>
      `
    }
  };

  const manualSections = userRole === 'admin' ? adminManualSections : clientManualSections;

  return (
    <div className="user-manual-overlay">
      <div className="user-manual-container">
        <div className="user-manual-header">
          <h2>User Manual - {userRole === 'admin' ? 'Administrator' : 'Client'}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="user-manual-content">
          <div className="user-manual-sidebar">
            {Object.keys(manualSections).map(sectionKey => (
              <div 
                key={sectionKey} 
                className={`manual-nav-item ${activeSection === sectionKey ? 'active' : ''}`}
                onClick={() => setActiveSection(sectionKey)}
              >
                {manualSections[sectionKey].title}
              </div>
            ))}
          </div>
          
          <div className="user-manual-main">
            <h2>{manualSections[activeSection].title}</h2>
            <div 
              className="manual-content" 
              dangerouslySetInnerHTML={{ __html: manualSections[activeSection].content }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManual;
